import { Tabs, Tooltip, Typography } from "antd"
import { useTranslation } from "react-i18next"
import { useSelector } from "react-redux"
import { useLocation, useNavigate } from "react-router-dom"

import { RoutesPaths } from "../../../../../constants/user-routes"
import Icon from "../../../../../Icon"
import { highAccessRoles } from "../../../../../Utils/permission"
import { RECEIPT_CONFIGURE_SLUGS } from "../../../constants/receipt-configure-slugs"
import { OVERLAY_STATUS_COPY } from "../ProfileStatusOverlay/ProfileStatusOverlay"

import styles from "./configureTabs.module.less"

const { Text } = Typography

export const RECEIPT_CONFIGURE_TABS = {
  PROFILE: "profile",
  BUSINESS: "business"
}

// `adminHref` is the same tab under Business settings, where support browses a
// merchant's Configure (CHA1-1975). Both lock flags describe merchant-side rules
// only — see the locking note in ConfigureTabs.
const CONFIGURE_TABS_CONFIG = [
  {
    key: RECEIPT_CONFIGURE_TABS.PROFILE,
    href: RoutesPaths.receipts.configure_profile_hash(RECEIPT_CONFIGURE_SLUGS.business_info),
    adminHref: RoutesPaths.receipts.admin_configure_profile_hash(
      RECEIPT_CONFIGURE_SLUGS.business_info
    ),
    label: "Profile",
    requireHighAccessRoleBA: true,
    lockOnOverlayStatus: true,
    testId: "deflect-configure-tab-profile"
  },
  {
    key: RECEIPT_CONFIGURE_TABS.BUSINESS,
    href: RoutesPaths.receipts.configure_business,
    adminHref: RoutesPaths.receipts.admin_configure_business,
    label: "Business",
    testId: "deflect-configure-tab-business"
  }
]

// `overlayStatus` is optional: it's the same server-driven lock ConfigureProfilePage
// derives from its wizard (see overlayStatusFromView) - passed in by whichever page
// currently has it loaded, since Configure state isn't guaranteed hydrated on every
// route that renders these tabs.
//
// `usersViewForAdmin` switches the tabs to support's copy of the page under Business
// settings (CHA1-1975). Two things change there: the hrefs point at the admin routes
// and carry the current `search` (the BA is a `business_account_id` query param, so
// dropping it would lose the account context), and neither lock applies - the BA-role
// gate is about a merchant's own role, and the profile lock exists to stop a merchant
// editing a submitted profile, while support is read-only on every step regardless.
const ConfigureTabs = ({ activeTab, overlayStatus, usersViewForAdmin }) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { search } = useLocation()
  const baRoleId = useSelector((s) => s.auth?.userInfo?.ba_role?.id)
  const isHighAccessRoleBA = !baRoleId || highAccessRoles?.includes(baRoleId)

  const tabsByKey = Object.fromEntries(CONFIGURE_TABS_CONFIG.map((tab) => [tab.key, tab]))

  const items = CONFIGURE_TABS_CONFIG.map(
    ({ key, label, testId, requireHighAccessRoleBA, lockOnOverlayStatus }) => {
      const roleLocked = !usersViewForAdmin && requireHighAccessRoleBA && !isHighAccessRoleBA
      const statusLocked = !usersViewForAdmin && lockOnOverlayStatus && !!overlayStatus
      const disabled = roleLocked || statusLocked
      const tooltipTitle = roleLocked
        ? "Available for Owner/Admin roles only"
        : statusLocked
          ? OVERLAY_STATUS_COPY[overlayStatus]?.title
          : null

      return {
        key,
        disabled,
        label: disabled ? (
          <Tooltip title={t(tooltipTitle)}>
            <Text type="secondary" data-testid={testId} className={styles.disabledLabel}>
              {t(label)}
              <Icon type="lock" className={styles.lockIcon} />
            </Text>
          </Tooltip>
        ) : (
          <span data-testid={testId}>{t(label)}</span>
        )
      }
    }
  )

  return (
    <Tabs
      className={styles.tabs}
      activeKey={activeTab}
      onChange={(key) => {
        const tab = tabsByKey[key]
        if (!tab) return
        if (usersViewForAdmin) {
          navigate({ pathname: tab.adminHref, search })
          return
        }
        if (tab.requireHighAccessRoleBA && !isHighAccessRoleBA) return
        if (tab.lockOnOverlayStatus && overlayStatus) return
        navigate(tab.href)
      }}
      items={items}
    />
  )
}

export default ConfigureTabs
